import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    Paper,
    Divider,
    Grid,
    Alert,
    CircularProgress,
} from '@mui/material';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../Cart';
import { useTranslation } from '../../i18n';
import cartService from '../../services/cartService';
import orderService, { CheckoutData, Order } from '../../services/orderService';
import api from '../../services/api';
import ReviewStep from './steps/ReviewStep';
import CustomerStep from './steps/CustomerStep';
import PaymentStep from './steps/PaymentStep';
import ConfirmationStep from './steps/ConfirmationStep';

export interface PaymentProvider {
    id: string;
    name: string;
    enabled: boolean;
}

export interface PaymentProviders {
    providers: PaymentProvider[];
    paypalClientId?: string;
    currency: string;
    allowFreeEnrollment: boolean;
}

const CheckoutPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { cart, totals, refreshCart, isCartFree } = useCart();
    const [activeStep, setActiveStep] = useState(0);
    const [customerData, setCustomerData] = useState<CheckoutData>({
        email: '',
        name: '',
        billingAddress: {},
    });
    const [order, setOrder] = useState<Order | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [paymentProviders, setPaymentProviders] = useState<PaymentProviders | null>(null);

    const steps = [
        t('checkout.steps.review'),
        t('checkout.steps.customer'),
        t('checkout.steps.payment'),
        t('checkout.steps.confirmation'),
    ];

    useEffect(() => {
        // Load payment providers
        const loadProviders = async () => {
            try {
                const response = await api.get<PaymentProviders>('/payments/providers');
                setPaymentProviders(response.data);
            } catch (err) {
                console.error('Error loading payment providers:', err);
            }
        };
        loadProviders();
    }, []);

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleCustomerSubmit = async (data: CheckoutData) => {
        setCustomerData(data);
        setLoading(true);
        setError(null);

        try {
            // Create the order
            const newOrder = await orderService.checkout(data);
            setOrder(newOrder);
            handleNext();
        } catch (err) {
            console.error('Error creating order:', err);
            setError(t('checkout.errors.orderCreationFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentComplete = async (completedOrder: Order) => {
        setOrder(completedOrder);
        await refreshCart();
        handleNext();
    };

    const handleFreeEnrollment = async () => {
        if (!order) return;

        setLoading(true);
        setError(null);

        try {
            const response = await api.post<{ success: boolean; order: Order }>(`/payments/free/${order.id}`);
            if (response.data.success) {
                handlePaymentComplete(response.data.order);
            }
        } catch (err) {
            console.error('Error processing free enrollment:', err);
            setError(t('checkout.errors.paymentFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoToCart = () => {
        navigate('/cart');
    };

    const handleContinueShopping = () => {
        navigate('/library');
    };

    const handleViewOrders = () => {
        navigate('/my-orders');
    };

    if (!cart || cart.items.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    {t('cart.empty.title')}
                </Alert>
                <Button
                    sx={{ mt: 2 }}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleContinueShopping}
                >
                    {t('cart.actions.continueShopping')}
                </Button>
            </Box>
        );
    }

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <ReviewStep
                        cart={cart}
                        totals={totals}
                        onBack={handleGoToCart}
                        onNext={handleNext}
                    />
                );
            case 1:
                return (
                    <CustomerStep
                        initialData={customerData}
                        loading={loading}
                        error={error}
                        onBack={handleBack}
                        onSubmit={handleCustomerSubmit}
                    />
                );
            case 2:
                return order ? (
                    <PaymentStep
                        order={order}
                        paymentProviders={paymentProviders}
                        isCartFree={isCartFree()}
                        loading={loading}
                        error={error}
                        onBack={handleBack}
                        onFreeEnrollment={handleFreeEnrollment}
                        onPaymentComplete={handlePaymentComplete}
                    />
                ) : (
                    <CircularProgress />
                );
            case 3:
                return order ? (
                    <ConfirmationStep
                        order={order}
                        onViewOrders={handleViewOrders}
                        onContinueShopping={handleContinueShopping}
                    />
                ) : (
                    <CircularProgress />
                );
            default:
                return null;
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartCheckoutIcon color="primary" />
                {t('checkout.title')}
            </Typography>

            <Box sx={{ mt: 3, mb: 4 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card>
                        <CardContent>
                            {renderStepContent()}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order Summary Sidebar */}
                {activeStep < 3 && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {t('checkout.summary.title')}
                            </Typography>

                            <Box sx={{ my: 2 }}>
                                {cart.items.map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                                            {item.product?.title || t('cart.unknownProduct')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {cartService.formatAmount(item.lineTotal, totals?.currency)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('checkout.summary.subtotal')}
                                </Typography>
                                <Typography variant="body2">
                                    {totals ? cartService.formatAmount(totals.subtotal, totals.currency) : '-'}
                                </Typography>
                            </Box>

                            {totals && totals.discount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('checkout.summary.discount')}
                                    </Typography>
                                    <Typography variant="body2" color="success.main">
                                        -{cartService.formatAmount(totals.discount, totals.currency)}
                                    </Typography>
                                </Box>
                            )}

                            {totals && totals.tax > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('checkout.summary.tax')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {cartService.formatAmount(totals.tax, totals.currency)}
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6">
                                    {t('checkout.summary.total')}
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {totals ? cartService.formatAmount(totals.total, totals.currency) : '-'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default CheckoutPage;
